import React, { useContext, useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import InsertContentLinkModal from 'components/Dashboard/components/InsertContentLinkModal';
import UploadHubModalAttach from 'components/UploadHub/UploadHubModalAttach';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import insertTrackableLinkPopup from 'components/Dashboard/helpers/insertTrackableLinkPopup';
import { insertListTinyEditor } from 'components/Dashboard/utilities/insertListTinyEditor';
import { toggleSignature } from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/emailSignatureFunctions';
import { store } from 'components/Store';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { validateEmail } from 'utils/validateEmail';
import useGetInitialEmailAddress from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/useGetInitialEmailAddress';
import { Portal } from 'react-portal';
import { blockquoteStyles, CustomColorPicker } from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import pasteHandler from 'components/Dashboard/Influence/CrmEmails/pasteHandler';
import { createLinkDetectionHandler, detectAndReplaceLinks } from 'components/Dashboard/helpers/linkDetectFunctions';
import applyTextColor from 'components/Dashboard/utilities/applyTextColor';
// import dynamicTextItems from 'components/Dashboard/Influence/CrmEmails/TinyEditor/dynamicTextItems';
import DynamicTextMenu, {
  openDynamicTextMenuFunction,
} from 'components/Dashboard/Influence/CrmEmails/TinyEditor/DynamicTextMenu';
import { buildEmailAttachmentHtml } from 'components/Dashboard/Influence/CrmEmails/buildEmailAttachmentHtml';

export default function TinyEditor({
  stateContent,
  handleBody,
  autoFocus,
  disabled,
  setEditorOnLoad = () => {},
  signatureEditor,
  emailState,
  setEmailState,
}) {
  const [showInsertContentModal, setShowInsertContentModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [bookmark, setBookmark] = useState(null);
  const [openColorPicker, setOpenColorPicker] = useState(false);
  const [openDynamicTextMenu, setOpenDynamicTextMenu] = useState(false);
  const [selectedColor, setSelectedColor] = useState('676a75');
  const editorRef = useRef(null);
  const atSignBookmarkRef = useRef(null);
  const onChange = (content) => {
    handleBody(content);
  };
  const isMobile = useIsMobile(480);
  const { getInitialSenderEmailAddress } = useGetInitialEmailAddress();
  const globalState = useContext(store);
  const { state: globalStateValue } = globalState;
  const emailControlsEnabled = !!emailState && !!setEmailState;
  const { currentEmailDetails } = emailState ?? {};
  const { listDomainsVerified } = globalStateValue;
  const { mailMergeFields, senderEmailAddressId } = currentEmailDetails ?? {};
  const emailAddresses = listDomainsVerified?.verifiedEmailAddresses;
  const imageToolbarControl = signatureEditor ? ' | insertImageBtn' : '';
  const emailToolbarControls = emailControlsEnabled ? ' | dynamicTextBtn signatureBtn | insertContentBtn' : '';
  const insertContentLink = (item) => {
    const contentLink =
      item.contentType === 'Tweet' ? item.contentUrl : `${import.meta.env.VITE_APP_APPURL}${item.contentUrl}`;
    const linkText = item.title;
    setShowInsertContentModal(false);
    editorRef.current.selection.select(editorRef.current.getBody(), true);
    editorRef.current.selection.collapse(false);
    editorRef.current.focus();
    editorRef.current.selection.bookmarkManager.moveToBookmark(bookmark);
    editorRef.current.execCommand(
      'mceInsertContent',
      false,
      `<a href=${contentLink} target="_blank" id="content_link">${linkText}</a>`
    );
  };

  const handleAttachFiles = (files) => {
    if (!files?.length) return;
    const html = buildEmailAttachmentHtml(files);
    editorRef.current.selection.select(editorRef.current.getBody(), true);
    editorRef.current.selection.collapse(false);
    editorRef.current.focus();
    editorRef.current.selection.bookmarkManager.moveToBookmark(bookmark);
    editorRef.current.execCommand('mceInsertContent', false, html);
  };

  const handleSignatureClick = () => {
    if (!emailControlsEnabled) {
      return;
    }
    let bodyString = String(editorRef.current.getBody().innerHTML);
    let sender = getInitialSenderEmailAddress(senderEmailAddressId, emailAddresses);
    let newBody = toggleSignature({
      emailAddressItem: sender,
      emailBody: bodyString,
    });
    let subject = document.getElementById('email-component-subject-input');
    setEmailState('currentEmailDetails', { ...currentEmailDetails, body: newBody, subject: subject.value });
  };

  const uploadImageHandler = (file, editor) => {
    let allowedImageTypesToInsert = ['image/png', 'image/gif', 'image/jpeg'];
    let fileType = file?.type;
    if (!allowedImageTypesToInsert.includes(fileType)) {
      return createNotification('warning', `Unsupported image type. Please use a JPG, PNG or GIF`);
    }
    const data = new FileReader();
    if (file?.name) {
      data.fileName = file.name;
    }

    const maxAllowedSizeMb = 0.2;
    if (file?.size > maxAllowedSizeMb * 1024 * 1024) {
      return createNotification('warning', `Max file size - ${maxAllowedSizeMb * 1000}Kb`);
    }
    data.onload = (e) => {
      editor.insertContent(`<img src="${data.result}" data-name="${data.fileName}" style="width:100px">`);
    };
    if (data && file && file.type.match('image.*')) {
      data.readAsDataURL(file);
    }
  };

  useEffect(() => {
    let buttons = document.querySelectorAll('.tox-tbtn');
    buttons.forEach((btn) => {
      btn.title = '';
    });
  }, [editorRef.current]);

  return (
    <>
      <InsertContentLinkModal
        showPopup={showInsertContentModal}
        setShowPopup={setShowInsertContentModal}
        insertContentLink={insertContentLink}
      />
      {emailControlsEnabled && (
        <UploadHubModalAttach
          openState={{ isOpen: showAttachModal, setIsOpen: setShowAttachModal }}
          onAttach={handleAttachFiles}
        />
      )}
      <Editor
        disabled={disabled}
        tinymceScriptSrc={import.meta.env.VITE_APP_APPURL + '/tinymce/tinymce.min.js'}
        licenseKey='gpl'
        onInit={(evt, editor) => {
          editorRef.current = editor;
          setEditorOnLoad(false);
        }}
        onFocus={(evt, editor) => {
          editor.selection.select(editor.getBody(), true);
          editor.selection.collapse(false);
          if (selectedColor !== '676a75') {
            applyTextColor(editor, selectedColor);
          }
          editor.selection.collapse(false);
        }}
        value={stateContent}
        init={{
          height: '100%',
          menubar: false,
          plugins: [
            'autolink',
            'lists',
            'link',
            'charmap',
            'preview',
            'anchor',
            'visualblocks',
            'code',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'code',
            'help',
            'wordcount',
          ],
          autolink_pattern: /^(https?:\/\/|ssh:\/\/|ftp:\/\/|file:\/|www\.)(.+)$/i,
          toolbar: disabled
            ? false
            : `fontfamily | bold | italic | strikethrough | attachmentBtn |  customLink | numlist bullist | customColorPicker | blockquote${imageToolbarControl}${emailToolbarControls}`,
          content_style: `@import url('https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&family=Work+Sans:wght@300&display=swap'); body { font-family: Arial, Helvetica, sans-serif; line-height:1.5; font-size:15px; padding-left:12px; margin-top:18px !important; margin-left:${
            isMobile ? 0 : 10
          }px; color:#00122b;} p {margin: 0} a{color:#0094cc} .crm-mail-merge-substitution{background-color: rgb(0, 148, 204, 0.08) !important; outline: none !important;cursor:default !important;} .email-attachment-item, .email-attachment-item *{outline: none !important; cursor: default !important;} .email-attachment-item a{cursor: pointer !important;} ${blockquoteStyles} .mce-content-body [data-mce-selected="inline-boundary"] {background-color: transparent !important;}}`,
          extended_valid_elements: 'span[*],svg[*],font[face|size|color|style]',
          toolbar_location: 'bottom',
          forced_root_block: 'p',
          forced_root_block_attrs: {
            style: 'font-family: Arial, Helvetica, sans-serif; font-size:15px; margin: 0; color: #00122b;',
          },
          link_assume_external_targets: 'http',
          convert_urls: false,
          link_title: false,
          anchor_top: false,
          anchor_bottom: false,
          font_family_formats:
            'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Lato=lato; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva',
          paste_preprocess: (editor, args) => {
            if (args.content.startsWith('<img')) {
              args.preventDefault();
            } else {
              args.content = pasteHandler(args.content);
            }
          },
          setup: function (editor) {
            editor.on('init', function (e) {
              if (autoFocus) {
                editor.selection.select(editor.getBody(), true);
                editor.selection.collapse(false);
                editor.focus();
              }
            });
            editor.on('NodeChange', function (e) {
              if (e.element.tagName === 'P') {
                const toolbarButtons = editor.editorContainer.querySelectorAll('.tox-tbtn');
                toolbarButtons[2]?.classList.remove('tox-tbtn--enabled');
                toolbarButtons[3]?.classList.remove('tox-tbtn--enabled');
              }
              if (emailControlsEnabled) {
                let signatureElem = editor.getBody().querySelector('#email_signature');
                if (signatureElem) {
                  if (signatureElem?.innerText?.trim()?.length === 0 && !signatureElem?.innerHTML?.includes('<img')) {
                    setSignatureBtnDataTip(false);
                    handleSignatureClick();
                  }
                }
              }
            });
            var urlInputValue = '';
            editor.on('ExecCommand', function (e) {
              if (e.command === 'mceLink') {
                let dialog = document.getElementsByClassName('tox-dialog');
                let dialogBackdrop = document.getElementsByClassName('tox-dialog-wrap__backdrop');
                setTimeout(() => {
                  insertTrackableLinkPopup(dialog, dialogBackdrop, editor);
                  if (dialog?.[0]) {
                    let inputField = dialog[0].querySelector('input[type="url"]');
                    urlInputValue = inputField.value;
                    inputField.oninput = (e) => (urlInputValue = e?.target?.value);
                  }
                }, 10);
              }
              if (e.command === 'mceFocus') {
                let dialog = document.getElementsByClassName('tox-dialog');
                if (dialog) {
                  setTimeout(() => {
                    if (dialog?.[0]?.innerHTML?.includes('Do you want to add the required mailto: prefix')) {
                      let emailIsValid = !!validateEmail(String(urlInputValue));
                      let action = emailIsValid ? 'Yes' : 'No';
                      dialog[0].querySelector(`.tox-button[data-mce-name=${action}]`).click();
                      urlInputValue = '';
                    }
                  }, 10);
                }
              }
            });
            editor.on('Drop', async (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              event.stopPropagation();
              uploadImageHandler(event?.dataTransfer?.files?.[0], editor);
            });
            editor.on('click', (event) => {
              const removeBtn = event.target.closest?.('.email-attachment-remove');
              if (removeBtn) {
                event.preventDefault();
                const item = removeBtn.closest('.email-attachment-item');
                if (item) {
                  editor.undoManager.transact(() => item.remove());
                  editor.fire('change');
                }
              }
            });
            var toggleState = false;
            let dynamicTextBtnApiRef = null;
            editor.ui.registry.addIcon(
              'dynamicText',
              `<span onmouseover="var visibleMenu = document.querySelector('.tox-tiered-menu'); if (!visibleMenu) { this.setAttribute('data-tooltip-content', 'Use attributes'); } else { this.removeAttribute('data-tooltip-content'); }" class='salutation-toolbar-button' data-tooltip-content="Use attributes" data-tooltip-id="general-tooltip" data-tooltip-class-name="header-tooltip pricing-tooltip-description" data-tooltip-events-off="click"><span>@</span></span>`
            );
            if (signatureEditor) {
              editor.ui.registry.addIcon(
                'insertImageIcon',
                `<img class='insert-image-toolbar-button' data-tooltip-content="Insert image" data-tooltip-id="settings-tooltip" data-tooltip-class-name="header-tooltip pricing-tooltip-description" src="${
                  import.meta.env.VITE_APP_CDNURL
                }/images/EditorIcons/insert_image.png" />`
              );
            }
            let setSignatureBtnDataTip = (active, api) => {
              let signatureBtn = document.querySelector('#signature_btn');
              if (signatureBtn) {
                let text = '';
                let sender = getInitialSenderEmailAddress(senderEmailAddressId, emailAddresses);
                if (!sender) {
                  text = "You'll be able to insert a signature once you select a 'From' address";
                  signatureBtn.setAttribute('data-tooltip-class-name', 'header-tooltip tooltip-width-220');
                  api?.setEnabled(false);
                } else if (!sender?.signature) {
                  text = 'You can create an email signature by going to Settings > Sender email addresses';
                  signatureBtn.setAttribute('data-tooltip-class-name', 'header-tooltip tooltip-width-260');
                  api?.setEnabled(false);
                } else {
                  text = `${active ? 'Remove' : 'Add'} signature`;
                  api?.setEnabled(true);
                  signatureBtn.setAttribute('data-tooltip-class-name', 'header-tooltip tooltip-width-220');
                }
                signatureBtn.setAttribute('data-tooltip-content', text);
              }
            };
            editor.ui.registry.addIcon(
              'signature',
              `<span id="signature_btn" data-tooltip-content="Signature" data-tooltip-id="general-tooltip" data-tooltip-class-name="header-tooltip tooltip-width-220"></span><img class="signature-toolbar-button" src="${
                import.meta.env.VITE_APP_CDNURL
              }/images/EditorIcons/signature_icon.png"/>`
            );
            editor.ui.registry.addIcon(
              'insertContent',
              `<img src="${
                import.meta.env.VITE_APP_CDNURL
              }/images/EditorIcons/insert_content.png" style="transform:translateY(1px); padding: 10px 5px 10px 12px" />`
            );
            editor.ui.registry.addButton('dynamicTextBtn', {
              icon: 'dynamicText',
              text: '',
              tooltip: 'Select color',
              onAction: (api) => {
                const currBookmark = editor.selection.bookmarkManager.getBookmark();
                setBookmark(currBookmark);
                setOpenDynamicTextMenu(!openDynamicTextMenu);
              },
            });
            editor.ui.registry.addToggleButton('signatureBtn', {
              type: 'button',
              text: ``,
              tooltip: '',
              icon: 'signature',
              onAction: function (api) {
                let state = editor.getBody().querySelector('#email_signature');
                setSignatureBtnDataTip(!state, api);
                handleSignatureClick();
              },
              onSetup: function (api) {
                let state = !editor.getBody().querySelector('#nosignature');
                setSignatureBtnDataTip(state, api);
              },
            });

            editor.ui.registry.addButton('insertContentBtn', {
              type: 'button',
              text: 'Insert content',
              tooltip: '',
              icon: 'insertContent',
              onAction: function () {
                toggleState = !toggleState;
                const currBookmark = editor.selection.bookmarkManager.getBookmark();
                setBookmark(currBookmark);
                setShowInsertContentModal(!showInsertContentModal);
              },
            });
            editor.ui.registry.addButton('attachmentBtn', {
              type: 'button',
              text: '',
              tooltip: 'Attach file',
              icon: 'attachment',
              onAction: function () {
                const currBookmark = editor.selection.bookmarkManager.getBookmark();
                setBookmark(currBookmark);
                setShowAttachModal(true);
              },
            });
            editor.ui.registry.addToggleButton('bold', {
              icon: 'bold',
              onAction: (api) => {
                editor.execCommand('mceToggleFormat', false, 'bold');
              },
              onSetup: function (api) {
                editor.formatter.formatChanged('bold', function (state) {
                  api.setActive(state);
                });
              },
            });
            editor.ui.registry.addToggleButton('italic', {
              icon: 'italic',
              onAction: (api) => {
                editor.execCommand('mceToggleFormat', false, 'italic');
              },
              onSetup: function (api) {
                editor.formatter.formatChanged('italic', function (state) {
                  api.setActive(state);
                });
              },
            });
            editor.ui.registry.addToggleButton('customLink', {
              icon: 'link',
              text: 'Insert Link',
              onAction: (api) => {
                editor.execCommand('mceLink', false, 'link');
              },
              onSetup: function (api) {
                editor.formatter.formatChanged('link', function (state) {
                  api.setActive(state);
                });
              },
            });
            // Add link detection handler
            const { handleKeyDown, expandCurrentLink } = createLinkDetectionHandler(editor);

            editor.on('KeyDown', (e) => {
              handleKeyDown(e);

              //OP: Start of @ sign detection-----------------------------------------//
              const textContent = editor.selection.getSel().anchorNode?.textContent;
              const textContentTrimmed =
                textContent && /\s$/.test(textContent) ? textContent.slice(0, -1) : textContent;
              const anchorOffset = editor.selection.getSel().anchorOffset;
              // Select current word at anchorOffset position
              if (
                textContent &&
                typeof anchorOffset === 'number' &&
                textContent.length === anchorOffset &&
                e.key === 'Backspace'
              ) {
                const anchorNode = editor.selection.getSel().anchorNode;

                // Find word boundaries
                let wordStart = anchorOffset;
                let wordEnd = anchorOffset;

                // Find start of word (go backwards until we hit whitespace or start of string)
                while (wordStart > 0 && !/\s/.test(textContentTrimmed[wordStart - 1])) {
                  wordStart--;
                }

                // Find end of word (go forwards until we hit whitespace or end of string)
                while (wordEnd < textContentTrimmed.length && !/\s/.test(textContentTrimmed[wordEnd])) {
                  wordEnd++;
                }

                // Select the word if it's not empty
                if (wordStart < wordEnd && anchorNode && anchorNode.nodeType === 3) {
                  const word = textContentTrimmed.substring(wordStart, wordEnd);
                  if (word.startsWith('@') && word.length !== 1) {
                    openDynamicTextMenuFunction(editor, dynamicTextBtnApiRef);
                  }
                }
              }

              if (emailControlsEnabled && (e.key === '@' || (e.keyCode === 50 && e.shiftKey))) {
                // Don't prevent default - let "@" be inserted
                // Store bookmark position right before "@" will be inserted
                const currentBookmark = editor.selection.bookmarkManager.getBookmark(2, true);
                atSignBookmarkRef.current = currentBookmark;

                // Use requestAnimationFrame for better timing
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    if (openDynamicTextMenuFunction && typeof openDynamicTextMenuFunction === 'function') {
                      openDynamicTextMenuFunction(editor, dynamicTextBtnApiRef);
                    }
                  }, 10);
                });
              }
              //OP: End of @ sign detection-----------------------------------------//
            });
            editor.on('KeyUp', (e) => {
              const { value, selectedColor } = editorRef.current.props ?? {};
              if (value === '' && selectedColor !== '676a75') {
                applyTextColor(editorRef.current, selectedColor);
              }
              insertListTinyEditor(e, editor);

              // Check if we're expanding an existing link
              if (window.isExpandingLink && window.currentExpandingLink) {
                // Continue expanding the current link
                expandCurrentLink(editor, window.currentExpandingLink);
              } else {
                // Add link detection on keyup
                detectAndReplaceLinks(editor);
              }
            });
            editor.ui.registry.addButton('customColorPicker', {
              icon: 'false',
              text: 'select color',
              tooltip: 'Select color',
              onAction: (api) => {
                const currBookmark = editor.selection.bookmarkManager.getBookmark();
                setBookmark(currBookmark);
                setOpenColorPicker(!openColorPicker);
              },
            });
          },
        }}
        onEditorChange={onChange}
      />
      {emailControlsEnabled && openDynamicTextMenu && (
        <Portal node={document && document.querySelector('body')}>
          <DynamicTextMenu
            setOpenDynamicTextMenu={setOpenDynamicTextMenu}
            editor={editorRef?.current}
            bookmark={bookmark}
            atSignBookmarkRef={atSignBookmarkRef}
            mailMergeFields={mailMergeFields}
          />
        </Portal>
      )}
      {openColorPicker && (
        <Portal node={document && document.querySelector('body')}>
          <CustomColorPicker
            setOpenColorPicker={setOpenColorPicker}
            editor={editorRef?.current}
            bookmark={bookmark}
            selectedColor={selectedColor}
            setSelectedColor={setSelectedColor}
            position={'top'}
          />
        </Portal>
      )}
    </>
  );
}
